import { useSyncExternalStore } from "react";
import { browserStorage } from "../lib/saved-logins";
import { createThemeStore, type Theme, type ThemeStore } from "../lib/theme";

// One store per page, created on first use (after `index.html` has applied
// the stored theme), shared by every toggle so they can never disagree.
let store: ThemeStore | null = null;
function themeStore(): ThemeStore {
  store ??= createThemeStore(document.documentElement, browserStorage(), window);
  return store;
}
const subscribe = (listener: () => void): (() => void) => themeStore().subscribe(listener);
const snapshot = (): Theme => themeStore().get();

/**
 * The light/dark switch. On desktop it sits beside the version chip as the
 * chip's twin (same 28px box, same rule and fill); on a phone, where the chip
 * is not shown, it sits in the More sheet at the right end of the studio row,
 * beside the studio's logo (36px). It holds a moon in light
 * (the way to dark) and a sun in dark. A toggle button: its name stays "Dark
 * mode" and `aria-pressed` says whether it is on; the tooltip names what a
 * click does.
 *
 * `index.html` has already applied the stored theme before first paint; the
 * shared store (`lib/theme.ts`) owns every change after that, including one
 * made in another tab.
 */
export function ThemeToggle(): JSX.Element {
  const theme = useSyncExternalStore(subscribe, snapshot);
  const dark = theme === "dark";
  return (
    <button
      type="button"
      className="theme-toggle"
      aria-label="Dark mode"
      aria-pressed={dark}
      title={dark ? "Light mode" : "Dark mode"}
      onClick={() => themeStore().set(dark ? "light" : "dark")}
    >
      {dark ? (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.5v2.6M12 18.9v2.6M2.5 12h2.6M18.9 12h2.6M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7z" />
        </svg>
      )}
    </button>
  );
}
